import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, SearchIcon, XIcon } from 'lucide-react';
import { demoBusinessTypes } from '../../data/requestDemo';
type BusinessTypeComboboxProps = {
  id: string;
  value: string;
  hasError: boolean;
  describedBy?: string;
  onChange: (value: string) => void;
  onBlur: () => void;
};
export function BusinessTypeCombobox({
  id,
  value,
  hasError,
  describedBy,
  onChange,
  onBlur
}: BusinessTypeComboboxProps) {
  const listId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const selected = demoBusinessTypes.find(t => t.label === value);
  const SelectedIcon = selected?.icon ?? SearchIcon;
  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || q === value.toLowerCase()) return demoBusinessTypes;
    return demoBusinessTypes.filter(t => t.label.toLowerCase().includes(q) || t.description.toLowerCase().includes(q));
  }, [query, value]);
  useEffect(() => setQuery(value), [value]);
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) close();
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  });
  function close() {
    if (!open) return;
    setOpen(false);
    setQuery(value);
    onBlur();
  }
  function choose(label: string) {
    onChange(label);
    setQuery(label);
    setOpen(false);
  }
  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight(h => Math.min(h + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      if (options[highlight]) choose(options[highlight].label);
    } else if (e.key === 'Escape') {
      close();
    }
  }
  return <div ref={wrapperRef} className="relative" data-id="element-409" data-mp="qzq42f9d4jq">
      <div className={`flex h-12 w-full items-center gap-3 rounded-[10px] border bg-white pl-4 pr-2 transition-[border-color,box-shadow] duration-150 ease-out ${open ? 'border-brand ring-4 ring-brand/15' : hasError ? 'border-[#C0362C]' : 'border-ink/15 hover:border-ink/30'}`} data-id="element-410" data-mp="rrm3ecrp4aj">
        <SelectedIcon className={`h-[18px] w-[18px] shrink-0 ${selected ? 'text-brand' : 'text-ink-muted'}`} aria-hidden="true" data-id="element-411" data-mp="5ngmfjwm2p7w" />
        <input id={id} type="text" role="combobox" autoComplete="off" aria-expanded={open} aria-controls={listId} aria-autocomplete="list" aria-invalid={hasError} aria-describedby={describedBy} aria-activedescendant={open && options[highlight] ? `${listId}-${highlight}` : undefined} placeholder="Search and select your business type" value={query} onFocus={() => setOpen(true)} onChange={e => {
        setQuery(e.target.value);
        setHighlight(0);
        setOpen(true);
        if (value) onChange('');
      }} onKeyDown={onKeyDown} className="min-w-0 flex-1 bg-transparent text-[16px] text-ink outline-none placeholder:text-ink-muted/70" data-id="element-412" data-mp="ei4qj6qga0v4" />
        {query && <button type="button" aria-label="Clear business type" onClick={() => {
        onChange('');
        setQuery('');
        setOpen(true);
      }} className="flex h-8 w-8 items-center justify-center rounded-[6px] text-ink-muted transition-colors duration-150 ease-out hover:bg-canvas-warm hover:text-ink" data-id="element-413" data-mp="t5v6jskhlsqm">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-414" data-mp="adant3yi540j" />
          </button>}
      </div>

      <AnimatePresence data-id="element-415" data-mp="1do6md8jb262b">
        {open && <motion.ul id={listId} role="listbox" initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute left-0 right-0 top-[calc(100%+8px)] z-30 max-h-[300px] overflow-y-auto rounded-[12px] border border-ink/10 bg-white p-1.5 shadow-[0_18px_40px_-16px_rgba(11,11,11,0.28)]" data-id="element-416" data-mp="18pdatlnkwp4s">
            {options.length === 0 ? <li className="px-3 py-4 text-center text-[14px] text-ink-muted" data-id="element-417" data-mp="l13sitpkqjie">
                No match. Choose <span className="font-medium text-ink" data-id="element-418" data-mp="g299bqnp2sxr">Other</span> instead.
              </li> : options.map((option, index) => {
          const Icon = option.icon;
          const isSelected = option.label === value;
          return <li key={option.label} id={`${listId}-${index}`} role="option" aria-selected={isSelected} onMouseEnter={() => setHighlight(index)} onMouseDown={e => {
            e.preventDefault();
            choose(option.label);
          }} className={`flex cursor-pointer items-center gap-3.5 rounded-[8px] px-3 py-2.5 transition-colors duration-100 ease-out ${index === highlight ? 'bg-brand-soft' : ''}`} data-id="element-419" data-mp="2hzjic6zkriny" data-mpi={index}>
                    <Icon className={`h-5 w-5 shrink-0 ${index === highlight ? 'text-brand' : 'text-ink-muted'}`} aria-hidden="true" data-id="element-420" data-mp="29064z38by9xy" />
                    <span className="min-w-0 flex-1" data-id="element-421" data-mp="3kdrwuw5kqpn6">
                      <span className="block text-[15px] font-medium text-ink" data-id="element-422" data-mp="3kfcq8ig5k2jg">{option.label}</span>
                      <span className="block truncate text-[13px] text-ink-muted" data-id="element-423" data-mp="3pefwg67vcet0">{option.description}</span>
                    </span>
                    {isSelected && <CheckIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-424" data-mp="1kbscuw3oou9x" />}
                  </li>;
        })}
          </motion.ul>}
      </AnimatePresence>
    </div>;
}